// src/parser/parseXne.ts
import { XMLParser } from "fast-xml-parser";

// src/errors.ts
var XONE_RENDERER_ERROR_CODES = {
  PARSE_INVALID_XML: "XONE.RENDERER.PARSE_INVALID_XML",
  PARSE_NO_ROOT_COLL: "XONE.RENDERER.PARSE_NO_ROOT_COLL",
  PARSE_MISSING_ATTR: "XONE.RENDERER.PARSE_MISSING_ATTR",
  PARSE_BAD_VISIBLE: "XONE.RENDERER.PARSE_BAD_VISIBLE",
  PARSE_ONCLICK_CHILD_NODE: "XONE.RENDERER.PARSE_ONCLICK_CHILD_NODE",
  RENDER_CONTAINER_MISSING: "XONE.RENDERER.RENDER_CONTAINER_MISSING",
  RENDER_NOT_INITIALIZED: "XONE.RENDERER.RENDER_NOT_INITIALIZED",
  RENDER_UNKNOWN_PROP: "XONE.RENDERER.RENDER_UNKNOWN_PROP",
  RENDER_UNKNOWN_GROUP: "XONE.RENDERER.RENDER_UNKNOWN_GROUP"
};
var XoneRendererError = class extends Error {
  code;
  detail;
  constructor(message, init) {
    super(message);
    this.name = "XoneRendererError";
    this.code = init.code;
    this.detail = init.detail ?? {};
    if (init.cause !== void 0) {
      this.cause = init.cause;
    }
  }
};

// src/visibility.ts
var VISIBILITY_BITS = {
  edit: 1,
  list: 2,
  contents: 4,
  combo: 8
};
function isVisibleInMode(visible, mode) {
  if (!Number.isFinite(visible)) return false;
  if (visible === 0) return false;
  const bit = VISIBILITY_BITS[mode];
  return (visible & bit) === bit;
}
function parseVisibleAttr(raw) {
  if (raw === void 0 || raw === null || raw === "") return 0;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 15) return 15;
  return n;
}

// src/parser/parseXne.ts
var OBJECT_EVENT_NAMES = /* @__PURE__ */ new Set([
  "create",
  "load",
  "unload",
  "before-edit",
  "after-edit",
  "resume-edit",
  "onback",
  "before-save",
  "after-save",
  "before-delete",
  "after-delete",
  "onchange",
  "selecteditem",
  "longpressitem"
]);
function createParser() {
  return new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "",
    attributesGroupName: "@_attrs",
    preserveOrder: true,
    // Importante: mantener strings tal cual (sin conversion implicita).
    parseTagValue: false,
    parseAttributeValue: false,
    trimValues: false,
    allowBooleanAttributes: true,
    textNodeName: "#text",
    commentPropName: "#comment",
    removeNSPrefix: false,
    // Aceptar tags vacios <prop ... />.
    unpairedTags: []
  });
}
function getAttrs(node) {
  const ataque = node[":@"];
  if (!ataque || typeof ataque !== "object") return {};
  const wrapper = ataque["@_attrs"];
  if (!wrapper || typeof wrapper !== "object") {
    const direct = {};
    for (const [k, v] of Object.entries(ataque)) {
      if (k === "@_attrs") continue;
      direct[k] = v === null || v === void 0 ? "" : String(v);
    }
    return direct;
  }
  const out = {};
  for (const [k, v] of Object.entries(wrapper)) {
    out[k] = v === null || v === void 0 ? "" : String(v);
  }
  return out;
}
function getTagName(node) {
  for (const k of Object.keys(node)) {
    if (k === ":@") continue;
    if (k.startsWith("@_")) continue;
    return k;
  }
  return "";
}
function getChildren(node, tagName) {
  const raw = node[tagName];
  if (!Array.isArray(raw)) return [];
  return raw;
}
function extractText(children) {
  const parts = [];
  for (const c of children) {
    const tag = getTagName(c);
    if (tag === "#text") {
      const v = c["#text"];
      if (typeof v === "string") parts.push(v);
      continue;
    }
    if (tag === "#comment") continue;
    const sub = getChildren(c, tag);
    if (sub.length > 0) parts.push(extractText(sub));
  }
  return parts.join("");
}
function extractEventScript(children) {
  for (const c of children) {
    const tag = getTagName(c);
    if (tag === "script") {
      return extractText(getChildren(c, "script"));
    }
  }
  return extractText(children);
}
function parseProp(node) {
  const attrs = getAttrs(node);
  const name = attrs["name"];
  if (!name) {
    throw new XoneRendererError(
      "Nodo <prop> sin atributo 'name'.",
      {
        code: XONE_RENDERER_ERROR_CODES.PARSE_MISSING_ATTR,
        detail: { tag: "prop", attribute: "name" }
      }
    );
  }
  const type = attrs["type"] ?? "T";
  const visibleRaw = attrs["visible"];
  const visible = parseVisibleAttr(visibleRaw);
  const children = getChildren(node, "prop");
  for (const c of children) {
    const tag = getTagName(c).toLowerCase();
    if (tag === "onclick") {
      throw new XoneRendererError(
        `Nodo <onclick> hijo no permitido en <prop name='${name}'>. onclick debe declararse SIEMPRE como atributo inline en <prop type='B'>.`,
        {
          code: XONE_RENDERER_ERROR_CODES.PARSE_ONCLICK_CHILD_NODE,
          detail: { propName: name }
        }
      );
    }
  }
  let onchangeScript;
  for (const c of children) {
    const tag = getTagName(c);
    if (tag === "onchange") {
      onchangeScript = extractEventScript(getChildren(c, "onchange"));
    }
  }
  const onclickRaw = attrs["onclick"];
  let onclickScript;
  if (typeof onclickRaw === "string" && onclickRaw.length > 0) {
    onclickScript = onclickRaw.replace(/^\s*javascript\s*:/i, "");
  }
  const methodRaw = attrs["method"];
  let methodEventName;
  if (typeof methodRaw === "string" && methodRaw.length > 0) {
    const m = /^\s*executenode\s*\(\s*([A-Za-z_][\w-]*)\s*\)\s*$/.exec(methodRaw);
    if (m) methodEventName = m[1];
  }
  const titleText = extractText(children).trim();
  const titleAttr = attrs["title"];
  const title = titleAttr ?? (titleText.length > 0 ? titleText : void 0);
  const groupAttr = attrs["group"];
  const group = groupAttr ? Number.parseInt(groupAttr, 10) : void 0;
  return {
    kind: "prop",
    name,
    type,
    title,
    visible,
    ...group !== void 0 && Number.isFinite(group) ? { group } : {},
    ...attrs["class"] !== void 0 ? { classCss: attrs["class"] } : {},
    ...onclickScript !== void 0 ? { onclickScript } : {},
    ...methodEventName !== void 0 ? { methodEventName } : {},
    ...attrs["contents"] !== void 0 ? { contentsName: attrs["contents"] } : {},
    ...attrs["viewmode"] !== void 0 ? { viewmode: attrs["viewmode"] } : {},
    attrs,
    ...onchangeScript !== void 0 ? { onchangeScript } : {}
  };
}
function parseContents(node) {
  const attrs = getAttrs(node);
  const name = attrs["name"];
  const src = attrs["src"];
  if (!name) {
    throw new XoneRendererError(
      "Nodo <contents> sin atributo 'name'.",
      {
        code: XONE_RENDERER_ERROR_CODES.PARSE_MISSING_ATTR,
        detail: { tag: "contents", attribute: "name" }
      }
    );
  }
  if (!src) {
    throw new XoneRendererError(
      "Nodo <contents> sin atributo 'src'.",
      {
        code: XONE_RENDERER_ERROR_CODES.PARSE_MISSING_ATTR,
        detail: { tag: "contents", attribute: "src" }
      }
    );
  }
  return {
    kind: "contents",
    name,
    src,
    ...attrs["filter"] !== void 0 ? { filter: attrs["filter"] } : {},
    ...attrs["sort"] !== void 0 ? { sort: attrs["sort"] } : {},
    attrs
  };
}
function parseFrame(node) {
  const attrs = getAttrs(node);
  const name = attrs["name"] ?? "";
  const children = getChildren(node, "frame");
  const out = [];
  for (const c of children) {
    const tag = getTagName(c);
    if (tag === "prop") out.push(parseProp(c));
    else if (tag === "frame") out.push(parseFrame(c));
    else if (tag === "contents") out.push(parseContents(c));
  }
  return {
    kind: "frame",
    name,
    ...attrs["class"] !== void 0 ? { classCss: attrs["class"] } : {},
    attrs,
    children: out
  };
}
function parseGroup(node) {
  const attrs = getAttrs(node);
  const name = attrs["name"] ?? "";
  const idAttr = attrs["id"];
  const id = idAttr ? Number.parseInt(idAttr, 10) : 0;
  const children = getChildren(node, "group");
  const frames = [];
  const props = [];
  const contents = [];
  for (const c of children) {
    const tag = getTagName(c);
    if (tag === "frame") frames.push(parseFrame(c));
    else if (tag === "prop") props.push(parseProp(c));
    else if (tag === "contents") contents.push(parseContents(c));
  }
  return {
    kind: "group",
    name,
    id: Number.isFinite(id) ? id : 0,
    ...attrs["title"] !== void 0 ? { title: attrs["title"] } : {},
    ...attrs["class"] !== void 0 ? { classCss: attrs["class"] } : {},
    attrs,
    frames,
    props,
    contents
  };
}
function parseEvent(node, tag, isLifecycle) {
  const attrs = getAttrs(node);
  const children = getChildren(node, tag);
  const scriptSource = extractEventScript(children);
  const refreshAttr = attrs["refresh"];
  const showWait = attrs["show-wait-dialog"] ?? attrs["showWaitDialog"];
  const waitText = attrs["wait-dialog-text"] ?? attrs["waitDialogText"];
  return {
    kind: "event",
    eventName: tag,
    scriptSource,
    isLifecycle,
    ...refreshAttr !== void 0 ? { refresh: refreshAttr === "true" || refreshAttr === "1" } : {},
    ...showWait !== void 0 ? { showWaitDialog: showWait === "true" || showWait === "1" } : {},
    ...waitText !== void 0 ? { waitDialogText: waitText } : {}
  };
}
function looksLikeCustomEvent(node, tag) {
  const children = getChildren(node, tag);
  if (children.length === 0) return false;
  for (const c of children) {
    const ct = getTagName(c);
    if (ct === "action") {
      const aattrs = getAttrs(c);
      if (aattrs["name"] === "runscript") {
        const grand = getChildren(c, "action");
        if (grand.some((g) => getTagName(g) === "script")) return true;
      }
    }
  }
  return false;
}
function parseCollNode(collNode) {
  const attrs = getAttrs(collNode);
  const name = attrs["name"];
  if (!name) {
    throw new XoneRendererError("<coll> sin atributo 'name'.", {
      code: XONE_RENDERER_ERROR_CODES.PARSE_MISSING_ATTR,
      detail: { tag: "coll", attribute: "name" }
    });
  }
  const groups = [];
  const rootContents = [];
  const events = [];
  const collChildren = getChildren(collNode, "coll");
  for (const c of collChildren) {
    const tag = getTagName(c);
    if (tag === "group") groups.push(parseGroup(c));
    else if (tag === "contents") rootContents.push(parseContents(c));
    else if (OBJECT_EVENT_NAMES.has(tag)) {
      events.push(parseEvent(c, tag, true));
    } else if (looksLikeCustomEvent(c, tag)) {
      events.push(parseEvent(c, tag, false));
    }
  }
  return {
    kind: "coll",
    name,
    ...attrs["title"] !== void 0 ? { title: attrs["title"] } : {},
    ...attrs["class"] !== void 0 ? { classCss: attrs["class"] } : {},
    attrs,
    groups,
    contents: rootContents,
    events
  };
}
function parseAppNode(appNode) {
  const attrs = getAttrs(appNode);
  const children = getChildren(appNode, "app");
  const styles = [];
  for (const c of children) {
    const tag = getTagName(c);
    if (tag === "style") {
      const sa = getAttrs(c);
      const url = sa["url"];
      if (!url) continue;
      styles.push({
        url,
        ...sa["encoding"] !== void 0 ? { encoding: sa["encoding"] } : {},
        attrs: sa
      });
    }
  }
  const debugRaw = attrs["debug"];
  const debug = debugRaw === void 0 ? void 0 : debugRaw === "true" || debugRaw === "1";
  const mainEntry = attrs["mainentry"] ?? attrs["main-entry"];
  const loginColl = attrs["login-coll"] ?? attrs["logincoll"];
  return {
    prefix: attrs["prefix"] ?? "",
    ...attrs["version"] !== void 0 ? { version: attrs["version"] } : {},
    ...debug !== void 0 ? { debug } : {},
    ...attrs["default-language"] !== void 0 ? { defaultLanguage: attrs["default-language"] } : {},
    ...mainEntry !== void 0 ? { mainEntry } : {},
    ...loginColl !== void 0 ? { loginColl } : {},
    styles,
    attrs
  };
}
function parseDocument(source) {
  if (typeof source !== "string" || source.length === 0) {
    throw new XoneRendererError("El parametro 'source' debe ser un string XML no vacio.", {
      code: XONE_RENDERER_ERROR_CODES.PARSE_INVALID_XML
    });
  }
  let parsed;
  try {
    parsed = createParser().parse(source);
  } catch (cause) {
    throw new XoneRendererError("XML invalido: no se pudo parsear.", {
      code: XONE_RENDERER_ERROR_CODES.PARSE_INVALID_XML,
      cause
    });
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new XoneRendererError("XML vacio o sin raiz.", {
      code: XONE_RENDERER_ERROR_CODES.PARSE_INVALID_XML
    });
  }
  return parsed;
}
function parseXne(source) {
  const parsed = parseDocument(source);
  const findCollIn = (nodes) => {
    for (const n of nodes) {
      const tag = getTagName(n);
      if (tag === "coll") return n;
    }
    return void 0;
  };
  let collNode = findCollIn(parsed);
  if (!collNode) {
    for (const n of parsed) {
      const tag = getTagName(n);
      if (tag === "xml") {
        collNode = findCollIn(getChildren(n, "xml"));
        if (collNode) break;
      }
    }
  }
  if (!collNode) {
    throw new XoneRendererError("No se encontro nodo raiz <coll> en el XML.", {
      code: XONE_RENDERER_ERROR_CODES.PARSE_NO_ROOT_COLL
    });
  }
  return parseCollNode(collNode);
}
function parseMappings(source) {
  const parsed = parseDocument(source);
  let container = parsed;
  for (const n of parsed) {
    const tag = getTagName(n);
    if (tag === "xml") {
      container = getChildren(n, "xml");
      break;
    }
  }
  let appConfig = null;
  let collpropsType;
  const collections = [];
  for (const n of container) {
    const tag = getTagName(n);
    if (tag === "app") {
      appConfig = parseAppNode(n);
      continue;
    }
    if (tag === "collprops") {
      const cpAttrs = getAttrs(n);
      if (cpAttrs["type"] !== void 0) collpropsType = cpAttrs["type"];
      const cpChildren = getChildren(n, "collprops");
      for (const c of cpChildren) {
        if (getTagName(c) === "coll") collections.push(parseCollNode(c));
      }
      continue;
    }
    if (tag === "coll") {
      collections.push(parseCollNode(n));
    }
  }
  if (appConfig === null && collections.length === 0) {
    throw new XoneRendererError(
      "El archivo no contiene <app> ni <coll>; no parece un mappings.xne valido.",
      { code: XONE_RENDERER_ERROR_CODES.PARSE_INVALID_XML }
    );
  }
  return {
    app: appConfig,
    ...collpropsType !== void 0 ? { collpropsType } : {},
    collections
  };
}

// src/binding/DataBinding.ts
function readControlValue(el, type) {
  if (type === "NC") {
    const input2 = el.querySelector('input[type="checkbox"]');
    return input2 ? input2.checked ? 1 : 0 : 0;
  }
  if (type === "N" || type === "F" || /^N\d$/.test(type)) {
    const input2 = el.querySelector("input");
    if (!input2) return null;
    const raw = input2.value;
    if (raw === "" || raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (type === "D" || type === "DT" || type === "H") {
    const input2 = el.querySelector("input");
    return input2 ? input2.value : null;
  }
  if (type === "IMG" || type === "B" || type === "Z" || type === "TL") return null;
  const input = el.querySelector("input, textarea");
  return input ? input.value : null;
}
function isNullish(value) {
  return value === void 0 || value === null;
}
function writeControlValue(el, type, value) {
  if (type === "NC") {
    const input2 = el.querySelector('input[type="checkbox"]');
    if (input2) {
      input2.checked = value === true || value === 1 || value === "1";
    }
    return;
  }
  if (type === "IMG") {
    const img = el.querySelector("img");
    if (img && typeof value === "string" && value.length > 0) img.src = value;
    return;
  }
  if (type === "B" || type === "Z") return;
  if (type === "TL") {
    if (isNullish(value)) return;
    const span = el.querySelector(".xone-label-text");
    if (span) span.textContent = String(value);
    return;
  }
  const displaySpan = el.querySelector("span.xone-text-value");
  if (displaySpan) {
    if (isNullish(value)) return;
    displaySpan.textContent = String(value);
    return;
  }
  const input = el.querySelector(
    "input, textarea"
  );
  if (input) {
    input.value = isNullish(value) ? "" : String(value);
  }
}
function bindControl(el, prop, dataObject) {
  if (prop.type === "B" || prop.type === "Z" || prop.type === "TL") {
    return () => {
    };
  }
  try {
    const value = dataObject.getValue(prop.name);
    writeControlValue(el, prop.type, value);
  } catch {
  }
  if (el.querySelector("span.xone-text-value")) {
    return () => {
    };
  }
  const target = el.querySelector(
    "input, textarea, select"
  );
  if (!target) {
    return () => {
    };
  }
  const handler = () => {
    try {
      const v = readControlValue(el, prop.type);
      dataObject.setValue(prop.name, v);
    } catch (err) {
      console.error("[xml-renderer] error bindeando", prop.name, err);
    }
  };
  target.addEventListener("input", handler);
  target.addEventListener("change", handler);
  return () => {
    target.removeEventListener("input", handler);
    target.removeEventListener("change", handler);
  };
}

// src/lifecycle/runEvents.ts
var RENDERER_LIFECYCLE_ORDER = [
  "create",
  "load",
  "before-edit",
  "after-edit",
  "onback",
  "before-save",
  "after-save",
  "before-delete",
  "after-delete"
];
async function runLifecycleEvent(eventName, opts) {
  const errors = [];
  let handlersExecuted = 0;
  let returnValue = void 0;
  const node = opts.events.find((e) => e.eventName === eventName);
  const ctx = {
    self: opts.self,
    ...opts.source !== void 0 ? { source: opts.source } : {},
    ...opts.locals !== void 0 ? { locals: opts.locals } : {}
  };
  if (node && node.scriptSource.trim().length > 0 && opts.runtime) {
    try {
      const ownerColl = opts.self.getOwnerCollection?.();
      const scope = {
        kind: "object",
        self: opts.self,
        This: opts.self,
        ThisDataColl: ownerColl,
        locals: opts.locals ?? {}
      };
      const r = await opts.runtime.executeScript(node.scriptSource, scope);
      handlersExecuted += 1;
      if (r === false) {
        return {
          canceled: true,
          returnValue: false,
          handlersExecuted,
          errors
        };
      }
      returnValue = r;
    } catch (err) {
      errors.push(err);
    }
  }
  try {
    const busResult = await opts.bus.emitObject(eventName, ctx);
    handlersExecuted += busResult.handlersExecuted;
    if (busResult.canceled) {
      return {
        canceled: true,
        returnValue: busResult.returnValue,
        handlersExecuted,
        errors: [...errors, ...busResult.errors]
      };
    }
    if (busResult.returnValue !== void 0) {
      returnValue = busResult.returnValue;
    }
    for (const e of busResult.errors) errors.push(e);
  } catch (err) {
    errors.push(err);
  }
  return {
    canceled: false,
    returnValue,
    handlersExecuted,
    errors
  };
}
async function runLifecycleChain(eventNames, opts) {
  let handlersExecuted = 0;
  const errors = [];
  let returnValue = void 0;
  for (const ev of eventNames) {
    const r = await runLifecycleEvent(ev, opts);
    handlersExecuted += r.handlersExecuted;
    for (const e of r.errors) errors.push(e);
    if (r.canceled) {
      return {
        canceled: true,
        returnValue: r.returnValue,
        handlersExecuted,
        errors
      };
    }
    returnValue = r.returnValue;
  }
  return { canceled: false, returnValue, handlersExecuted, errors };
}
function registerEvents(collectionName, events, registry) {
  for (const ev of events) {
    if (!ev.scriptSource || ev.scriptSource.trim().length === 0) continue;
    registry.register({
      eventName: ev.eventName,
      collectionName,
      scriptSource: ev.scriptSource,
      ...ev.refresh !== void 0 ? { refreshAfter: ev.refresh } : {},
      ...ev.showWaitDialog !== void 0 ? { showWaitDialog: ev.showWaitDialog } : {},
      ...ev.waitDialogText !== void 0 ? { waitDialogText: ev.waitDialogText } : {}
    });
  }
}

// src/renderer/applyVisualAttributes.ts
var HEX_8 = /^#([0-9a-fA-F]{8})$/;
var HEX_6 = /^#([0-9a-fA-F]{6})$/;
var SIZE_TOKEN = /^(-?\d+(?:\.\d+)?)(p|%)?$/;
function toCssSize(raw) {
  if (raw === void 0 || raw === null) return null;
  const trimmed = String(raw).trim();
  if (trimmed.length === 0) return null;
  const m = SIZE_TOKEN.exec(trimmed);
  if (!m) {
    return trimmed;
  }
  const n = Number(m[1]);
  const unit = m[2];
  if (unit === "%") return `${n}%`;
  if (unit === "p") return `${n}px`;
  if (n === -1) return "100%";
  if (n === -2) return "auto";
  if (n < 0) return "auto";
  return `${n}px`;
}
function toCssColor(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (HEX_6.test(trimmed)) return trimmed.toUpperCase();
  const m8 = HEX_8.exec(trimmed);
  if (m8) {
    const hex = m8[1].toUpperCase();
    const a = hex.slice(0, 2);
    const r = hex.slice(2, 4);
    const g = hex.slice(4, 6);
    const b = hex.slice(6, 8);
    return `#${r}${g}${b}${a}`;
  }
  return trimmed;
}
function parseAlign(raw) {
  if (!raw) return null;
  const parts = String(raw).trim().toLowerCase().split("|").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  let h = null;
  let v = null;
  let centerCount = 0;
  for (const token of parts) {
    if (token === "left" || token === "right") {
      h = token;
    } else if (token === "top" || token === "bottom") {
      v = token;
    } else if (token === "middle") {
      v = "center";
    } else if (token === "center") {
      centerCount++;
    }
  }
  if (centerCount > 0) {
    if (centerCount >= 2) {
      if (!h) h = "center";
      if (!v) v = "center";
    } else if (h && !v) {
      v = "center";
    } else if (v && !h) {
      h = "center";
    } else if (!h && !v) {
      h = "center";
      v = "center";
    }
  }
  if (h === null && v === null) return null;
  return { horizontal: h, vertical: v };
}
var FLEX_JUSTIFY = {
  left: "flex-start",
  center: "center",
  right: "flex-end"
};
var FLEX_ALIGN = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end"
};
function applyVisualAttributes(el, attrs, options) {
  const width = toCssSize(attrs["width"]);
  if (width !== null) el.style.width = width;
  const height = toCssSize(attrs["height"]);
  if (height !== null) {
    el.style.height = height;
  }
  if (options.kind === "container" && attrs["scroll"]?.toLowerCase() === "true") {
    const horizontal = attrs["orientation"]?.toLowerCase() === "horizontal";
    if (horizontal) {
      el.style.overflowX = "auto";
      el.style.overflowY = "hidden";
    } else {
      el.style.overflowY = "auto";
      el.style.overflowX = "hidden";
    }
  }
  const bg = toCssColor(attrs["bgcolor"]);
  if (bg) el.style.backgroundColor = bg;
  const fg = toCssColor(attrs["forecolor"]);
  if (fg) el.style.color = fg;
  const margins = [
    ["tmargin", "marginTop"],
    ["bmargin", "marginBottom"],
    ["lmargin", "marginLeft"],
    ["rmargin", "marginRight"],
    ["tpadding", "paddingTop"],
    ["bpadding", "paddingBottom"],
    ["lpadding", "paddingLeft"],
    ["rpadding", "paddingRight"]
  ];
  for (const [attr, cssProp] of margins) {
    const v = toCssSize(attrs[attr]);
    if (v !== null) el.style[cssProp] = v;
  }
  const radius = toCssSize(attrs["border-corner-radius"]);
  if (radius !== null) el.style.borderRadius = radius;
  const borderColor = toCssColor(attrs["border-color"]);
  if (borderColor) el.style.borderColor = borderColor;
  const borderWidth = toCssSize(attrs["border-width"]);
  if (borderWidth !== null) {
    el.style.borderWidth = borderWidth;
    if (!el.style.borderStyle) el.style.borderStyle = "solid";
  }
  if (attrs["framebox"]?.toLowerCase() === "false") {
    el.style.border = "none";
  }
  const fontSize = toCssSize(attrs["fontsize"]);
  if (fontSize !== null) el.style.fontSize = fontSize;
  const fontName = attrs["fontname"];
  if (fontName) el.style.fontFamily = fontName;
  if (attrs["textfont-bold"]?.toLowerCase() === "true" || attrs["fontbold"]?.toLowerCase() === "true") {
    el.style.fontWeight = "bold";
  }
  if (attrs["textfont-italic"]?.toLowerCase() === "true" || attrs["fontitalic"]?.toLowerCase() === "true") {
    el.style.fontStyle = "italic";
  }
  const textAlign = attrs["text-align"];
  if (textAlign === "left" || textAlign === "center" || textAlign === "right" || textAlign === "justify") {
    el.style.textAlign = textAlign;
  }
  const align = parseAlign(attrs["align"]);
  if (align) {
    if (options.kind === "container") {
      el.style.display = "flex";
      el.style.flexDirection = "column";
      if (align.vertical) {
        el.style.justifyContent = FLEX_ALIGN[align.vertical] ?? "flex-start";
      }
      if (align.horizontal) {
        el.style.alignItems = FLEX_JUSTIFY[align.horizontal] ?? "stretch";
      }
      if (align.horizontal === "center") {
        el.style.alignSelf = "center";
      } else if (align.horizontal === "left") {
        el.style.alignSelf = "flex-start";
      } else if (align.horizontal === "right") {
        el.style.alignSelf = "flex-end";
      }
    } else {
      if (align.horizontal) {
        const h = align.horizontal;
        el.style.alignSelf = FLEX_JUSTIFY[h] ?? "stretch";
        if (h === "center") {
          el.style.marginInline = "auto";
        }
        if (!textAlign) {
          el.style.textAlign = h;
        }
      }
      if (align.vertical === "center") {
        el.style.marginBlock = "auto";
      } else if (align.vertical === "top") {
        el.style.marginBlockEnd = "auto";
      } else if (align.vertical === "bottom") {
        el.style.marginBlockStart = "auto";
      }
    }
  }
}

// src/renderer/renderControl.ts
function makeRoot(prop) {
  const root = document.createElement("div");
  root.classList.add("xone-prop", `xone-prop-${prop.type.toLowerCase()}`);
  if (prop.classCss) {
    for (const c of prop.classCss.split(/\s+/).filter(Boolean)) {
      root.classList.add(c);
    }
  }
  root.dataset["propName"] = prop.name;
  root.dataset["propType"] = prop.type;
  applyVisualAttributes(root, prop.attrs, { kind: "leaf" });
  return root;
}
function shouldShowLabel(prop) {
  const lw = prop.attrs["labelwidth"];
  if (lw !== void 0 && lw.trim() === "0") return false;
  if (prop.attrs["floating-tooltip"]?.toLowerCase() === "true") return false;
  return !!prop.title;
}
function maybeAppendTitle(root, prop) {
  if (!shouldShowLabel(prop)) return;
  const lbl = document.createElement("label");
  lbl.className = "xone-prop-title";
  lbl.textContent = prop.title ?? "";
  root.appendChild(lbl);
}
function appendFloatingInput(root, inp, prop) {
  const floating = prop.attrs["floating-tooltip"]?.toLowerCase() === "true";
  const tooltip = prop.attrs["tooltip"] ?? prop.title ?? "";
  if (!floating || !tooltip) {
    if (tooltip) inp.placeholder = tooltip;
    root.appendChild(inp);
    return;
  }
  const wrap = document.createElement("div");
  wrap.className = "xone-field xone-field-floating";
  inp.placeholder = " ";
  wrap.appendChild(inp);
  const lbl = document.createElement("label");
  lbl.className = "xone-field-label";
  lbl.textContent = tooltip;
  wrap.appendChild(lbl);
  root.appendChild(wrap);
}
function appendPasswordToggle(wrap, inp) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "xone-password-toggle";
  btn.setAttribute("aria-label", "Mostrar/ocultar contrase\xF1a");
  btn.innerHTML = "\u{1F441}";
  btn.addEventListener("click", () => {
    const showing = inp.type === "text";
    inp.type = showing ? "password" : "text";
    btn.classList.toggle("is-visible", !showing);
  });
  wrap.appendChild(btn);
}
function isDisplayOnlyByXone(prop, viewOnly) {
  if (viewOnly) return true;
  if (prop.attrs["locked"]?.toLowerCase() === "true") return true;
  const disableedit = prop.attrs["disableedit"];
  if (disableedit !== void 0 && disableedit.trim() !== "") return true;
  return false;
}
function renderAsDisplayOnly(root, prop) {
  root.classList.add("is-display-only");
  maybeAppendTitle(root, prop);
  const span = document.createElement("span");
  span.className = "xone-text-value";
  span.dataset["propName"] = prop.name;
  span.textContent = prop.title ?? "";
  root.appendChild(span);
  return root;
}
function renderControl(prop, opts = {}) {
  const root = makeRoot(prop);
  const t = prop.type.toUpperCase();
  const viewOnly = opts.viewOnly ?? false;
  switch (t) {
    case "B": {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "xone-btn";
      btn.textContent = prop.title ?? prop.name;
      if (prop.attrs["img"]) {
        const img = document.createElement("img");
        img.alt = "";
        img.src = prop.attrs["img"];
        btn.prepend(img);
      }
      if (prop.classCss) {
        for (const c of prop.classCss.split(/\s+/).filter(Boolean)) {
          btn.classList.add(c);
        }
      }
      const bg = toCssColor(prop.attrs["bgcolor"]);
      if (bg) {
        btn.style.backgroundColor = bg;
        root.style.backgroundColor = "";
      }
      const fg = toCssColor(prop.attrs["forecolor"]);
      if (fg) btn.style.color = fg;
      const radius = toCssSize(prop.attrs["border-corner-radius"]);
      if (radius !== null) btn.style.borderRadius = radius;
      const fontSize = toCssSize(prop.attrs["fontsize"]);
      if (fontSize !== null) btn.style.fontSize = fontSize;
      if (prop.attrs["textfont-bold"]?.toLowerCase() === "true") {
        btn.style.fontWeight = "bold";
      }
      btn.style.width = "100%";
      btn.style.height = "100%";
      root.appendChild(btn);
      return root;
    }
    case "TL": {
      const span = document.createElement("span");
      span.className = "xone-label-text";
      span.textContent = prop.title ?? "";
      root.appendChild(span);
      return root;
    }
    case "THTML": {
      const div = document.createElement("div");
      div.className = "xone-html";
      root.appendChild(div);
      return root;
    }
    case "IMG":
    case "PH": {
      maybeAppendTitle(root, prop);
      const rawPath = prop.attrs["path"] ?? prop.attrs["img"] ?? prop.attrs["src"];
      const resolvedSrc = rawPath ? /^(\/|https?:|data:)/.test(rawPath) ? rawPath : `/icons/${rawPath}` : null;
      const fallbackTitle = prop.title ?? "";
      const showFallback = () => {
        root.classList.add("xone-img-fallback");
        const span = document.createElement("span");
        span.className = "xone-img-fallback-text";
        span.textContent = fallbackTitle;
        root.appendChild(span);
      };
      if (resolvedSrc) {
        const img = document.createElement("img");
        img.alt = fallbackTitle || prop.name;
        img.className = "xone-img";
        img.addEventListener("error", () => {
          img.remove();
          if (fallbackTitle) showFallback();
        }, { once: true });
        img.src = resolvedSrc;
        root.appendChild(img);
      } else if (fallbackTitle) {
        showFallback();
      }
      return root;
    }
    case "NC": {
      const lbl = document.createElement("label");
      lbl.className = "xone-checkbox-wrapper";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      lbl.appendChild(cb);
      if (prop.title) {
        const span = document.createElement("span");
        span.textContent = prop.title;
        lbl.appendChild(span);
      }
      root.appendChild(lbl);
      return root;
    }
    case "X": {
      if (isDisplayOnlyByXone(prop, viewOnly)) {
        return renderAsDisplayOnly(root, prop);
      }
      maybeAppendTitle(root, prop);
      const inp = document.createElement("input");
      inp.type = "password";
      inp.name = prop.name;
      inp.autocomplete = "current-password";
      const wantsToggle = prop.attrs["show-password-visibility-toggle"]?.toLowerCase() === "true";
      const floating = prop.attrs["floating-tooltip"]?.toLowerCase() === "true";
      const tooltip = prop.attrs["tooltip"] ?? prop.title ?? "";
      if (floating && tooltip) {
        const wrap = document.createElement("div");
        wrap.className = "xone-field xone-field-floating";
        inp.placeholder = " ";
        wrap.appendChild(inp);
        const lbl = document.createElement("label");
        lbl.className = "xone-field-label";
        lbl.textContent = tooltip;
        wrap.appendChild(lbl);
        if (wantsToggle) appendPasswordToggle(wrap, inp);
        root.appendChild(wrap);
      } else {
        if (tooltip) inp.placeholder = tooltip;
        if (wantsToggle) {
          const wrap = document.createElement("div");
          wrap.className = "xone-field";
          wrap.appendChild(inp);
          appendPasswordToggle(wrap, inp);
          root.appendChild(wrap);
        } else {
          root.appendChild(inp);
        }
      }
      return root;
    }
    case "N":
    case "F":
    case "N1":
    case "N2":
    case "N3":
    case "N4":
    case "N5":
    case "N6": {
      if (isDisplayOnlyByXone(prop, viewOnly)) {
        return renderAsDisplayOnly(root, prop);
      }
      maybeAppendTitle(root, prop);
      const inp = document.createElement("input");
      inp.type = "number";
      inp.name = prop.name;
      root.appendChild(inp);
      return root;
    }
    case "D": {
      if (isDisplayOnlyByXone(prop, viewOnly)) {
        return renderAsDisplayOnly(root, prop);
      }
      maybeAppendTitle(root, prop);
      const inp = document.createElement("input");
      inp.type = "date";
      inp.name = prop.name;
      root.appendChild(inp);
      return root;
    }
    case "DT": {
      if (isDisplayOnlyByXone(prop, viewOnly)) {
        return renderAsDisplayOnly(root, prop);
      }
      maybeAppendTitle(root, prop);
      const inp = document.createElement("input");
      inp.type = "datetime-local";
      inp.name = prop.name;
      root.appendChild(inp);
      return root;
    }
    case "H":
    case "TT": {
      if (isDisplayOnlyByXone(prop, viewOnly)) {
        return renderAsDisplayOnly(root, prop);
      }
      maybeAppendTitle(root, prop);
      const inp = document.createElement("input");
      inp.type = "time";
      inp.name = prop.name;
      root.appendChild(inp);
      return root;
    }
    case "Z": {
      const container = document.createElement("div");
      container.className = "xone-contents";
      if (prop.contentsName) container.dataset["contents"] = prop.contentsName;
      if (prop.viewmode) container.dataset["viewmode"] = prop.viewmode;
      root.appendChild(container);
      return root;
    }
    default: {
      if (isDisplayOnlyByXone(prop, viewOnly)) {
        return renderAsDisplayOnly(root, prop);
      }
      const lw = prop.attrs["labelwidth"]?.trim();
      const floating = prop.attrs["floating-tooltip"]?.toLowerCase() === "true";
      const tooltip = prop.attrs["tooltip"];
      const noBorder = prop.attrs["border"]?.toLowerCase() === "false";
      const isHeuristicDisplayOnly = (lw === "0" || lw === "1") && !floating && !tooltip && !prop.classCss?.includes("textoEditable");
      if (isHeuristicDisplayOnly) {
        return renderAsDisplayOnly(root, prop);
      }
      maybeAppendTitle(root, prop);
      const inp = document.createElement("input");
      inp.type = "text";
      inp.name = prop.name;
      inp.autocomplete = "off";
      if (noBorder) inp.style.border = "none";
      appendFloatingInput(root, inp, prop);
      return root;
    }
  }
}

// src/renderer/XmlRenderer.ts
var XmlRenderer = class _XmlRenderer {
  bus;
  registry;
  runtime;
  astResolver;
  contentsResolver;
  ast;
  container;
  dataObject;
  mode = "edit";
  viewOnly = false;
  propEntries = /* @__PURE__ */ new Map();
  groupEntries = /* @__PURE__ */ new Map();
  subRenderers = /* @__PURE__ */ new Map();
  constructor(deps) {
    this.bus = deps.bus;
    this.registry = deps.registry;
    this.runtime = deps.runtime;
    this.astResolver = deps.astResolver;
    this.contentsResolver = deps.contentsResolver;
  }
  // -------------------------------------------------------------------------
  // API sincrona
  // -------------------------------------------------------------------------
  getNode(propName) {
    return this.propEntries.get(propName)?.el;
  }
  getDataObject() {
    return this.dataObject;
  }
  getCollection() {
    return this.ast;
  }
  // -------------------------------------------------------------------------
  // API asincrona
  // -------------------------------------------------------------------------
  /**
   * Renderiza el AST en `container`. Ejecuta el ciclo `create -> load` con
   * `await` secuencial. Si CUALQUIERA cancela (`return false`), el render se
   * mantiene pero la cancelacion se propaga via el `EventResult` agregado.
   */
  async render(ast, container, dataObject, options = {}) {
    if (!container) {
      throw new XoneRendererError("'container' es obligatorio para render().", {
        code: XONE_RENDERER_ERROR_CODES.RENDER_CONTAINER_MISSING
      });
    }
    this.ast = ast;
    this.container = container;
    this.dataObject = dataObject;
    this.mode = options.mode ?? "edit";
    this.viewOnly = options.viewOnly ?? false;
    this.teardownState();
    container.replaceChildren();
    const collEl = document.createElement("section");
    collEl.className = "xone-coll";
    if (ast.classCss) {
      for (const c of ast.classCss.split(/\s+/).filter(Boolean)) {
        collEl.classList.add(c);
      }
    }
    collEl.dataset["collName"] = ast.name;
    applyVisualAttributes(collEl, ast.attrs, { kind: "container" });
    container.appendChild(collEl);
    if (this.registry) {
      registerEvents(ast.name, ast.events, this.registry);
    }
    for (const group of ast.groups) {
      const groupEl = this.renderGroup(group);
      collEl.appendChild(groupEl);
      this.groupEntries.set(group.name, { group, el: groupEl });
    }
    for (const c of ast.contents) {
      const ph = document.createElement("div");
      ph.className = "xone-contents-root";
      ph.dataset["contentsName"] = c.name;
      ph.dataset["contentsSrc"] = c.src;
      collEl.appendChild(ph);
    }
    for (const entry of this.propEntries.values()) {
      const unbind = bindControl(entry.el, entry.prop, dataObject);
      entry.unbind = unbind;
    }
    this.wireButtons();
    if (options.runLifecycle === false) {
      return {
        canceled: false,
        returnValue: void 0,
        handlersExecuted: 0,
        errors: []
      };
    }
    return await runLifecycleChain(["create", "load"], {
      collectionName: ast.name,
      events: ast.events,
      bus: this.bus,
      ...this.runtime !== void 0 ? { runtime: this.runtime } : {},
      ...this.registry !== void 0 ? { registry: this.registry } : {},
      self: dataObject
    });
  }
  /**
   * Infla los `<prop type="Z">` con `contents="@..."`: resuelve la coleccion
   * destino vía `contentsResolver`, instancia un sub-renderer para cada
   * item y lo monta dentro del placeholder ya creado por `renderControl`.
   *
   * Layout:
   *   - `viewmode="gridview"` + `columns="N"`: CSS grid de N columnas.
   *   - `viewmode="recyclerview"` (default) o ausente: lista vertical.
   *   - Cualquier otro viewmode: lista vertical como fallback.
   *
   * Cada item es un `<article class="xone-content-item">` que contiene el
   * AST de la colección destino renderizado en modo `contents` (bitmask 4).
   * Los click en items invocan el evento `<selecteditem>` de la coll destino
   * si esta declarado, pasando el DataObject correspondiente como `self`.
   *
   * Idempotente: invocar dos veces sustituye los items previos.
   */
  async inflateContents() {
    if (!this.ast || !this.contentsResolver || !this.astResolver) return;
    const ast = this.ast;
    const contentsByName = /* @__PURE__ */ new Map();
    for (const c of ast.contents) {
      contentsByName.set(c.name, {
        src: c.src,
        ...c.filter !== void 0 ? { filter: c.filter } : {},
        ...c.sort !== void 0 ? { sort: c.sort } : {}
      });
    }
    for (const [propName, entry] of this.propEntries.entries()) {
      if (entry.prop.type.toUpperCase() !== "Z") continue;
      const contentsName = entry.prop.contentsName;
      if (!contentsName) continue;
      const decl = contentsByName.get(contentsName);
      if (!decl) {
        console.warn(
          `[xml-renderer] <prop type="Z"> '${propName}' referencia contents '${contentsName}' que no existe en la coll '${ast.name}'.`
        );
        continue;
      }
      const childAst = this.astResolver(decl.src);
      if (!childAst) {
        console.warn(
          `[xml-renderer] AST de coleccion '${decl.src}' no encontrado (referenciado desde '${ast.name}' / contents '${contentsName}').`
        );
        continue;
      }
      try {
        const items = await this.contentsResolver(decl.src, {
          ...decl.filter !== void 0 ? { filter: decl.filter } : {},
          ...decl.sort !== void 0 ? { sort: decl.sort } : {}
        });
        await this.renderContentsItems(
          entry.el,
          entry.prop,
          childAst,
          items
        );
      } catch (err) {
        console.error(
          `[xml-renderer] Fallo inflando contents '${propName}' (src=${decl.src}):`,
          err
        );
      }
    }
  }
  /**
   * Renderiza los items resueltos de un `<contents>` dentro del placeholder
   * del prop Z. Layout segun `viewmode`.
   */
  async renderContentsItems(propRoot, prop, childAst, items) {
    const container = propRoot.querySelector(".xone-contents");
    if (!container) return;
    container.replaceChildren();
    const viewmode = (prop.viewmode ?? "recyclerview").toLowerCase();
    container.dataset["viewmode"] = viewmode;
    container.classList.remove("is-gridview", "is-recyclerview");
    if (viewmode === "gridview") {
      container.classList.add("is-gridview");
      const cols = Number(prop.attrs["columns"] ?? "2");
      container.style.display = "grid";
      container.style.gridTemplateColumns = `repeat(${Number.isFinite(cols) && cols > 0 ? cols : 2}, 1fr)`;
      container.style.gap = "8px";
    } else {
      container.classList.add("is-recyclerview");
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.gap = "6px";
    }
    if (items.length === 0) {
      if (prop.attrs["show-no-data"]?.toLowerCase() !== "false") {
        const empty = document.createElement("div");
        empty.className = "xone-contents-empty";
        empty.textContent = "No hay datos";
        container.appendChild(empty);
      }
      return;
    }
    const selectedItemEvent = childAst.events.find((e) => e.eventName === "selecteditem");
    const editInRow = prop.attrs["edit-inrow"]?.toLowerCase() === "true";
    const subViewOnly = !editInRow;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const itemEl = document.createElement("article");
      itemEl.className = "xone-content-item";
      itemEl.dataset["index"] = String(i);
      container.appendChild(itemEl);
      const sub = new _XmlRenderer({
        bus: this.bus,
        ...this.registry !== void 0 ? { registry: this.registry } : {},
        ...this.runtime !== void 0 ? { runtime: this.runtime } : {},
        ...this.astResolver !== void 0 ? { astResolver: this.astResolver } : {},
        ...this.contentsResolver !== void 0 ? { contentsResolver: this.contentsResolver } : {}
      });
      this.subRenderers.set(`${prop.name}#${i}`, sub);
      try {
        await sub.render(childAst, itemEl, item, {
          mode: "contents",
          runLifecycle: false,
          viewOnly: subViewOnly
        });
      } catch (err) {
        console.error(`[xml-renderer] sub-render item ${i} fallo:`, err);
      }
      if (selectedItemEvent && selectedItemEvent.scriptSource && this.runtime) {
        itemEl.style.cursor = "pointer";
        const script = selectedItemEvent.scriptSource;
        const runtime = this.runtime;
        itemEl.addEventListener("click", () => {
          void runtime.executeScript(script, {
            kind: "object",
            self: item,
            This: item,
            locals: { propName: prop.name, index: i }
          }).catch((err) => {
            console.error(
              `[xml-renderer] selecteditem '${childAst.name}' fallo:`,
              err
            );
          });
        });
      }
    }
  }
  /**
   * Ejecuta un evento del ciclo bajo demanda (por ejemplo `before-save` antes
   * de persistir). Devuelve el `EventResult`; el llamador decide si abortar.
   */
  async runEvent(eventName) {
    this.assertInitialized();
    const ast = this.ast;
    return await runLifecycleEvent(eventName, {
      collectionName: ast.name,
      events: ast.events,
      bus: this.bus,
      ...this.runtime !== void 0 ? { runtime: this.runtime } : {},
      ...this.registry !== void 0 ? { registry: this.registry } : {},
      self: this.dataObject
    });
  }
  /** Refresca el valor de un control desde el `DataObject`. */
  async refreshValue(propName) {
    this.assertInitialized();
    const entry = this.propEntries.get(propName);
    if (!entry) {
      throw new XoneRendererError(`Prop desconocido: '${propName}'.`, {
        code: XONE_RENDERER_ERROR_CODES.RENDER_UNKNOWN_PROP,
        detail: { propName }
      });
    }
    const value = this.dataObject.getValue(propName);
    writeControlValue(entry.el, entry.prop.type, value);
    const onchange = entry.prop.onchangeScript;
    if (onchange && this.runtime) {
      await this.runtime.executeScript(onchange, {
        kind: "object",
        self: this.dataObject,
        This: this.dataObject,
        locals: { propName, value }
      });
    }
  }
  /**
   * Refresca todos los controles del render actual. Si un prop concreto
   * no es un campo XOne válido (botones `btnAtras`, frames inline, etc.
   * cuyo nombre arranca por minúscula) o si su lectura lanza, el bucle
   * captura y continúa con el siguiente prop en vez de romper el refresh
   * completo de la pantalla. Paridad con Android, donde un getter fallido
   * se ignora y la vista sigue refrescándose.
   */
  async refreshAll() {
    this.assertInitialized();
    for (const propName of this.propEntries.keys()) {
      try {
        await this.refreshValue(propName);
      } catch {
      }
    }
  }
  /** Muestra/oculta un grupo. SYNC en el plan; en TS asincronizamos por
   *  paridad con el resto del ciclo y por si en el futuro emitimos eventos. */
  async setGroupVisible(groupName, visible) {
    this.assertInitialized();
    const entry = this.groupEntries.get(groupName);
    if (!entry) {
      throw new XoneRendererError(`Group desconocido: '${groupName}'.`, {
        code: XONE_RENDERER_ERROR_CODES.RENDER_UNKNOWN_GROUP,
        detail: { groupName }
      });
    }
    entry.el.hidden = !visible;
    entry.el.style.display = visible ? "" : "none";
  }
  /** Lock/unlock grupo: deshabilita los controles editables. */
  async setGroupLocked(groupName, locked) {
    this.assertInitialized();
    const entry = this.groupEntries.get(groupName);
    if (!entry) {
      throw new XoneRendererError(`Group desconocido: '${groupName}'.`, {
        code: XONE_RENDERER_ERROR_CODES.RENDER_UNKNOWN_GROUP,
        detail: { groupName }
      });
    }
    entry.el.dataset["locked"] = locked ? "true" : "false";
    const inputs = entry.el.querySelectorAll("input, button, textarea, select");
    for (const i of Array.from(inputs)) i.disabled = locked;
  }
  /** Destruye el renderer y libera listeners/sub-renderers. */
  destroy() {
    this.teardownState();
    if (this.container) this.container.replaceChildren();
    this.ast = void 0;
    this.container = void 0;
    this.dataObject = void 0;
  }
  // -------------------------------------------------------------------------
  // Implementacion interna
  // -------------------------------------------------------------------------
  assertInitialized() {
    if (!this.ast || !this.container || !this.dataObject) {
      throw new XoneRendererError("XmlRenderer no inicializado: llamar a render() primero.", {
        code: XONE_RENDERER_ERROR_CODES.RENDER_NOT_INITIALIZED
      });
    }
  }
  teardownState() {
    for (const entry of this.propEntries.values()) {
      try {
        entry.unbind();
      } catch {
      }
    }
    this.propEntries.clear();
    this.groupEntries.clear();
    for (const sub of this.subRenderers.values()) sub.destroy();
    this.subRenderers.clear();
  }
  renderGroup(group) {
    const el = document.createElement("section");
    el.className = "xone-group";
    if (group.classCss) {
      for (const c of group.classCss.split(/\s+/).filter(Boolean)) {
        el.classList.add(c);
      }
    }
    el.dataset["groupName"] = group.name;
    el.dataset["groupId"] = String(group.id);
    if (group.title) el.dataset["groupTitle"] = group.title;
    applyVisualAttributes(el, group.attrs, { kind: "container" });
    for (const p of group.props) this.appendPropIfVisible(el, p);
    for (const f of group.frames) el.appendChild(this.renderFrame(f));
    for (const c of group.contents) el.appendChild(this.renderContentsPlaceholder(c));
    return el;
  }
  renderFrame(frame) {
    const el = document.createElement("div");
    el.className = "xone-frame";
    if (frame.classCss) {
      for (const c of frame.classCss.split(/\s+/).filter(Boolean)) {
        el.classList.add(c);
      }
    }
    el.dataset["frameName"] = frame.name;
    applyVisualAttributes(el, frame.attrs, { kind: "container" });
    const frameOnclick = frame.attrs["onclick"];
    if (frameOnclick && this.runtime) {
      el.style.cursor = "pointer";
      el.addEventListener("click", () => {
        void this.runtime.executeScript(frameOnclick.replace(/^\s*javascript\s*:/i, ""), {
          kind: "object",
          self: this.dataObject,
          This: this.dataObject,
          locals: { frameName: frame.name }
        }).catch((err) => {
          console.error(`[xml-renderer] frame onclick '${frame.name}' error:`, err);
        });
      });
    }
    this.appendChildrenRowAware(el, frame.children);
    return el;
  }
  /**
   * Itera los hijos de un frame agrupando los que tienen `newline="false"`
   * en una "fila" (`<div class="xone-row">` con `display: flex; row`). XOne
   * apila los hijos verticalmente por defecto (newline implícito = true) y
   * sólo arranca una fila nueva cuando el hijo declara explícitamente
   * `newline="false"`.
   *
   * Caso típico en EntradaApp:
   *   <frame name="frmHero">
   *     <prop name="MAP_HERO_ICON" .../>            ← arranca fila 1
   *     <frame name="frmHeroInner" newline="false"/> ← se une a fila 1
   *   </frame>
   * Resultado: icono y texto del header lado a lado (no apilados).
   */
  appendChildrenRowAware(parent, children) {
    const parentAlignItems = parent.style.alignItems;
    const parentJustify = parent.style.justifyContent;
    const propagatedJustify = parentAlignItems === "center" ? "center" : parentAlignItems === "flex-end" ? "flex-end" : parentAlignItems === "flex-start" ? "flex-start" : "";
    const propagatedAlign = parentJustify === "center" ? "center" : parentJustify === "flex-end" ? "flex-end" : parentJustify === "flex-start" ? "flex-start" : "";
    let currentRow = null;
    for (const child of children) {
      const isContinuation = "attrs" in child && typeof child.attrs["newline"] === "string" && child.attrs["newline"].toLowerCase() === "false";
      const childEl = this.renderChildFor(child, parent);
      if (!childEl) continue;
      if (isContinuation && currentRow !== null) {
        currentRow.appendChild(childEl);
        continue;
      }
      currentRow = document.createElement("div");
      currentRow.className = "xone-row";
      if (propagatedJustify) {
        currentRow.style.justifyContent = propagatedJustify;
      }
      if (propagatedAlign) {
        currentRow.style.alignItems = propagatedAlign;
      }
      currentRow.appendChild(childEl);
      parent.appendChild(currentRow);
    }
  }
  /**
   * Construye el elemento DOM para un hijo individual de un frame, aplicando
   * la lógica de visibilidad / registro de prop. Devuelve `null` si el hijo
   * es invisible en el modo actual (en cuyo caso el caller lo omite).
   *
   * Importante: `parent` se pasa solo para registrar el prop en el mapa de
   * entries; el elemento NO se anexa aquí — el caller decide en qué row va.
   */
  renderChildFor(child, _parentForRegistration) {
    if (child.kind === "prop") {
      if (!isVisibleInMode(child.visible, this.mode)) return null;
      const el = renderControl(child, { viewOnly: this.viewOnly });
      this.propEntries.set(child.name, {
        prop: child,
        el,
        unbind: () => {
        }
      });
      return el;
    }
    if (child.kind === "frame") return this.renderFrame(child);
    if (child.kind === "contents") return this.renderContentsPlaceholder(child);
    return null;
  }
  appendPropIfVisible(parent, prop) {
    if (!isVisibleInMode(prop.visible, this.mode)) {
      return;
    }
    const el = renderControl(prop, { viewOnly: this.viewOnly });
    parent.appendChild(el);
    this.propEntries.set(prop.name, {
      prop,
      el,
      unbind: () => {
      }
    });
  }
  renderContentsPlaceholder(c) {
    const el = document.createElement("div");
    el.className = "xone-contents-block";
    el.dataset["contentsName"] = c.name;
    el.dataset["contentsSrc"] = c.src;
    if (c.filter) el.dataset["contentsFilter"] = c.filter;
    if (c.sort) el.dataset["contentsSort"] = c.sort;
    return el;
  }
  /**
   * Conecta los `<prop type='B'>` con su `onclickScript` (atributo inline).
   * El script se ejecuta en el runtime XOne (sandbox), por lo que NO tiene
   * acceso al DOM. Si no hay runtime, se emite por el bus.
   */
  wireButtons() {
    for (const [propName, entry] of this.propEntries.entries()) {
      if (entry.prop.type.toUpperCase() !== "B") continue;
      const onclick = entry.prop.onclickScript;
      const methodEventName = entry.prop.methodEventName;
      if (!onclick && !methodEventName) continue;
      const btn = entry.el.querySelector("button");
      if (!btn) continue;
      btn.addEventListener("click", () => {
        const self = this.dataObject;
        void this.dispatchButtonAction(propName, onclick, methodEventName, self);
      });
    }
  }
  /**
   * Ejecuta secuencialmente, si estan presentes, el `onclick` inline y el
   * evento custom referenciado por `method="executenode(<name>)"`. Ambos
   * comparten contexto (`self`/`This`) y se ejecutan en el runtime XOne.
   */
  async dispatchButtonAction(propName, onclick, methodEventName, self) {
    if (onclick) {
      if (this.runtime) {
        try {
          await this.runtime.executeScript(onclick, {
            kind: "object",
            self,
            This: self,
            locals: { propName }
          });
        } catch (err) {
          console.error(`[xml-renderer] onclick '${propName}' error:`, err);
        }
      } else {
        await this.bus.emitControl("OnClick", {
          Sender: this.propEntries.get(propName)?.el,
          ...self !== void 0 ? { self } : {},
          locals: { propName }
        });
      }
    }
    if (methodEventName) {
      const ast = this.ast;
      if (!ast) return;
      const event = ast.events.find((e) => e.eventName === methodEventName);
      if (!event || !event.scriptSource) {
        console.warn(
          `[xml-renderer] method='executenode(${methodEventName})' en '${propName}': evento '<${methodEventName}>' no encontrado en la coleccion '${ast.name}'.`
        );
        return;
      }
      if (!this.runtime) {
        console.warn(
          `[xml-renderer] runtime no inyectado; no se puede ejecutar method='${methodEventName}'.`
        );
        return;
      }
      try {
        await this.runtime.executeScript(event.scriptSource, {
          kind: "object",
          self,
          This: self,
          locals: { propName, eventName: methodEventName }
        });
      } catch (err) {
        console.error(
          `[xml-renderer] method '${methodEventName}' (boton '${propName}') error:`,
          err
        );
      }
    }
  }
};
function createXmlRenderer(deps) {
  return new XmlRenderer(deps);
}

// src/renderer/baselineCss.ts
var BASELINE_CSS = String.raw`
/* ────────────────────────────────────────────────────────────────────────
   XOne SPA — baseline (inyectado por @xone-web/xml-renderer)
   ──────────────────────────────────────────────────────────────────────── */

*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; min-height: 100%; }
body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                 "Helvetica Neue", Arial, sans-serif;
    color: #2D3436;
    background: #FFFFFF;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

/* Container app: ocupa toda la ventana. */
#app { min-height: 100vh; display: flex; flex-direction: column; }

/* ── Coll / Group / Frame: flex column por defecto ── */
.xone-coll,
.xone-group,
.xone-frame {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}
.xone-coll { flex: 1 1 auto; min-height: 100%; }

/* Row wrapper: agrupa hijos consecutivos con newline="false". */
.xone-row {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    width: 100%;
    gap: 0;
    flex: 0 0 auto;
}
/* Las rows heredan el flex-grow de su contenido, salvo si el frame
   contenedor tiene altura explicita (height="320p", etc), en cuyo caso la
   primera row ocupa todo el alto disponible. */
.xone-frame[style*="height"] > .xone-row:only-child { flex: 1 1 auto; }

/* ── Prop: bloque vertical (stack), ocupa ancho del padre por defecto ── */
.xone-prop {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
}

/* Label encima del input (modo no-floating). */
.xone-prop-title {
    font-size: 12px;
    color: #757575;
    margin-bottom: 4px;
}

/* ── Inputs nativos: reset y heredan colores/tipografía del root ── */
.xone-prop input,
.xone-prop textarea,
.xone-prop select {
    width: 100%;
    height: 100%;
    min-height: 40px;
    padding: 12px 14px;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    background: transparent;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    outline: none;
    transition: border-color .15s ease, box-shadow .15s ease;
}
.xone-prop input:focus,
.xone-prop textarea:focus,
.xone-prop select:focus {
    border-color: #FF6B9D;
    box-shadow: 0 0 0 3px rgba(255, 107, 157, .15);
}

/* Type-specific: labels (.xone-prop-tl) son texto puro, sin marco. */
.xone-prop-tl {
    display: block;
    width: 100%;
}
.xone-prop-tl .xone-label-text {
    display: block;
    width: 100%;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    line-height: 1.2;
}

/* Display-only T: cuando un <prop type="T"> se usa como texto (labelwidth=0
   sin floating-tooltip, o locked=true), lo renderizamos como span sin marco
   ni padding de input editable. */
.xone-prop-t.is-display-only,
.xone-prop-t.is-display-only span.xone-text-value {
    display: block;
    width: 100%;
}
.xone-prop-t.is-display-only span.xone-text-value {
    color: inherit;
    font: inherit;
    text-align: inherit;
    line-height: 1.2;
    background: transparent;
    border: none;
    padding: 0;
}

/* Botones. */
.xone-prop-b { display: flex; }
.xone-btn {
    appearance: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    padding: 0 16px;
    border-radius: 8px;
    background: #2D3436;
    color: #FFFFFF;
    transition: filter .15s ease, transform .05s ease;
    min-height: 44px;
}
.xone-btn:hover { filter: brightness(1.08); }
.xone-btn:active { transform: translateY(1px); }

/* ── Floating label field (.xone-field-floating) ── */
.xone-field {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
}
.xone-field input {
    width: 100%;
    height: 100%;
    padding: 22px 14px 8px;
}
.xone-field-floating .xone-field-label {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    color: #9AA0A6;
    pointer-events: none;
    transition: top .15s ease, font-size .15s ease, color .15s ease,
                transform .15s ease;
    background: transparent;
    padding: 0 4px;
}
.xone-field-floating input:focus + .xone-field-label,
.xone-field-floating input:not(:placeholder-shown) + .xone-field-label {
    top: 10px;
    transform: translateY(0);
    font-size: 11px;
    color: #FF6B9D;
}

/* Password toggle. */
.xone-password-toggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 18px;
    color: #9AA0A6;
    padding: 6px;
    border-radius: 50%;
    line-height: 1;
}
.xone-password-toggle:hover { color: #2D3436; }
.xone-password-toggle.is-visible { color: #FF6B9D; }

/* Checkboxes / labels nativos. */
.xone-prop-nc .xone-checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}
.xone-prop-nc input[type="checkbox"] {
    width: 18px;
    height: 18px;
    min-height: unset;
    padding: 0;
    margin: 0;
}

/* Imágenes. */
.xone-img {
    max-width: 100%;
    max-height: 100%;
    height: auto;
    width: auto;
    object-fit: contain;
    display: block;
    margin: 0 auto;
}
.xone-prop-img,
.xone-prop-ph {
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Fallback tipográfico cuando IMG no tiene path o falla al cargar.
   Hereda forecolor/fontsize/textfont-bold/text-align aplicados al root. */
.xone-img-fallback .xone-img-fallback-text {
    display: inline-block;
    font: inherit;
    color: inherit;
    line-height: 1;
    text-align: inherit;
    font-weight: inherit;
}

/* Contents placeholder (default: column). El sub-renderer aplica
   display:grid o display:flex inline segun viewmode (gridview / recyclerview),
   sobrescribiendo estos defaults. */
.xone-contents, .xone-contents-root {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}
.xone-content-item {
    display: flex;
    flex-direction: column;
    width: 100%;
    cursor: default;
}
.xone-content-item:hover { filter: brightness(0.97); }
.xone-contents-empty {
    color: #9AA0A6;
    text-align: center;
    padding: 16px;
    font-size: 12px;
}

/* Portal de diálogos modales (controlado por @xone-web/ui). */
#xone-dialog-portal {
    position: fixed; inset: 0; z-index: 1000; pointer-events: none;
}
#xone-dialog-portal > * { pointer-events: auto; }
`;
export {
  BASELINE_CSS,
  RENDERER_LIFECYCLE_ORDER,
  VISIBILITY_BITS,
  XONE_RENDERER_ERROR_CODES,
  XmlRenderer,
  XoneRendererError,
  applyVisualAttributes,
  bindControl,
  createXmlRenderer,
  isVisibleInMode,
  parseAlign,
  parseMappings,
  parseVisibleAttr,
  parseXne,
  readControlValue,
  registerEvents,
  runLifecycleChain,
  runLifecycleEvent,
  toCssColor,
  toCssSize,
  writeControlValue
};
//# sourceMappingURL=index.js.map